/**
 * Foundation Formatting - date, ora, data/ora (formato italiano).
 *
 * STANDARD UFFICIALE Gruppo Impresa Suite:
 *   Data:     "gg/mm/aaaa"
 *   Ora:      "HH:mm"
 *   Data/Ora: "gg/mm/aaaa HH:mm"
 *
 * Nessun altro formato e' ammesso nelle superfici visibili all'utente.
 * L'ISO 8601 e' riservato all'uso tecnico interno (API JSON, ordinamento).
 */

export const DATE_FORMAT_LABEL = "gg/mm/aaaa";
export const TIME_FORMAT_LABEL = "HH:mm";
export const DATETIME_FORMAT_LABEL = "gg/mm/aaaa HH:mm";
export const DATE_LONG_FORMAT_LABEL = "giorno d mese aaaa";

const _WEEKDAYS_IT = [
  "domenica", "lunedì", "martedì", "mercoledì",
  "giovedì", "venerdì", "sabato",
];
const _MONTHS_IT = [
  "gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno",
  "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre",
];

const _ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * Restituisce true se la stringa e' formalmente una data ISO YYYY-MM-DD
 * (con eventuale suffisso orario). Utility CORE Suite: nessuna semantica
 * di dominio, solo validazione formato.
 */
export function isIsoDate(str) {
  return typeof str === "string" && _ISO_DATE_RE.test(str);
}

/**
 * Parser locale sicuro di "YYYY-MM-DD" (evita l'off-by-one da timezone).
 * Se l'input non e' ISO, usa il parser nativo.
 */
export function parseISOLocal(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  const s = String(value);
  const m = s.match(_ISO_DATE_RE);
  if (m) {
    // Estrae date + eventuale orario locale
    const [, yStr, mStr, dStr] = m;
    const y = Number(yStr);
    const mo = Number(mStr) - 1;
    const d = Number(dStr);
    // Se contiene orario, tenta il parse completo
    if (s.length > 10 && s[10] === "T") {
      const dt = new Date(s);
      if (!isNaN(dt.getTime())) return dt;
    }
    return new Date(y, mo, d);
  }
  const dt = new Date(s);
  return isNaN(dt.getTime()) ? null : dt;
}

/** Formatta una data in "gg/mm/aaaa". */
export function formatDateIT(value, fallback = "") {
  const dt = parseISOLocal(value);
  if (!dt) return fallback;
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const yy = dt.getFullYear();
  return `${dd}/${mm}/${yy}`;
}

/** Formatta un orario in "HH:mm". */
export function formatTimeIT(value, fallback = "") {
  const dt = parseISOLocal(value);
  if (!dt) return fallback;
  const hh = String(dt.getHours()).padStart(2, "0");
  const mi = String(dt.getMinutes()).padStart(2, "0");
  return `${hh}:${mi}`;
}

/** Formatta un istante in "gg/mm/aaaa HH:mm". */
export function formatDateTimeIT(value, fallback = "") {
  const dt = parseISOLocal(value);
  if (!dt) return fallback;
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const yy = dt.getFullYear();
  const hh = String(dt.getHours()).padStart(2, "0");
  const mi = String(dt.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yy} ${hh}:${mi}`;
}

/**
 * Formatta una data in forma lunga italiana (es. "giovedì 5 gennaio 2026").
 * Rispetta lo Standard Suite: usato SOLO per titoli/label estese, mai per
 * campi tabellari (che usano formatDateIT).
 */
export function formatDateLongIT(value, fallback = "") {
  const dt = parseISOLocal(value);
  if (!dt) return fallback;
  const wd = _WEEKDAYS_IT[dt.getDay()];
  const dd = dt.getDate();
  const mo = _MONTHS_IT[dt.getMonth()];
  const yy = dt.getFullYear();
  return `${wd} ${dd} ${mo} ${yy}`;
}


/**
 * P2.0 STEP C1 · K7 · Cataloghi mese/settimana capitalizzati per UI Suite.
 *
 * `MONTHS_IT` e `WEEKDAYS_SHORT` sono i cataloghi CANONICI usati nelle
 * intestazioni dei componenti Suite (MonthPicker, calendar grid, KPI headers,
 * tabelle mensili). Sono espliciti e capitalizzati per essere pronti al render
 * senza ulteriori trasformazioni.
 *
 * Non confondere con `_MONTHS_IT` interno di `formatDateLongIT` (lowercase per
 * frasi tipo "giovedì 5 gennaio 2026").
 */
export const MONTHS_IT = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];

export const WEEKDAYS_SHORT = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
